<template>
    <div>
        <PageHeader :subtitle="$t('dataExport.description')">
            {{ $t('dataExport.title') }}
        </PageHeader>

        <div class="bg-light rounded shadow-sm mb-4">
            <b-form inline class="mb-3 gap-2">
                <b-form-group :label="$t('year')" label-for="select-year">
                    <b-form-select id="select-year" v-model="selectedYear" class="ml-3" :options="availableYears" :disabled="computedLoading" />
                </b-form-group>

                <b-form-group :label="$t('month')" label-for="select-month" class="ml-3">
                    <b-form-select id="select-month" v-model="selectedMonth" class="ml-3" :options="availableMonths" :disabled="computedLoading" />
                </b-form-group>

                <b-button variant="primary" class="ml-3" :disabled="computedLoading || generating" :loading="generating" @click="startGeneratingReport">
                    {{ $t('dataExport.generate') }}
                </b-button>
            </b-form>

            <ErrorMessage :value="error" />

            <Card v-if="!computedLoading" class="mb-4" :header="$t('dataExport.availableReports')">
                <DataTable
                    :items="reports"
                    :fields="reportFields"
                    :hover="true"
                    :loading="computedLoading"
                    :total-rows="reports.length"
                    :per-page="10"
                    sort-by="generatedAt"
                    sort-direction="DESC"
                    no-padding
                >
                    <!-- Progress Cell -->
                    <template #cell(progress)="data">
                        <b-progress v-if="isReportPending(data.item)" :value="data.item.progress" height="8px" class="mb-0 mt-1" />
                    </template>

                    <!-- Actions Cell -->
                    <template #cell(actions)="data">
                        <div class="d-flex align-items-center justify-content-end gap-2">
                            <Tooltip v-if="isReportCorrupted(data.item)" :text="$t('dataExport.errorTooltip')" placement="top" />

                            <ButtonIcon
                                size="sm"
                                :disabled="!isReportDownloadable(data.item)"
                                :loading="downloadingIds.includes(data.item.id)"
                                @click="downloadReport(data.item, 'EXPORT_SPACES_REPORTS')"
                            >
                                {{ $t('dataExport.download') }}
                            </ButtonIcon>
                        </div>
                    </template>
                </DataTable>
            </Card>

            <ReportGroup
                title="dataExport.availablePriceReports"
                :items="priceReports"
                :loading="computedLoading"
                :downloading-ids="downloadingIds"
                @download="r => downloadReport(r, 'EXPORT_PRODUCTS_PRICE_REPORTS')"
            />

            <ReportGroup
                title="dataExport.availableCompetitionReports"
                :items="competitionReports"
                :loading="computedLoading"
                :downloading-ids="downloadingIds"
                @download="r => downloadReport(r, 'EXPORT_COMPETITION_REPORTS')"
            />

            <ReportGroup
                title="dataExport.availableAvailabilityReports"
                :items="availabilityReports"
                :loading="computedLoading"
                :downloading-ids="downloadingIds"
                @download="r => downloadReport(r, 'EXPORT_PRODUCTS_AVAILABILITY_REPORTS')"
            />
        </div>
    </div>
</template>

<script>
    import { getYear, getMonth } from 'date-fns';
    import PageHeader from '@/components/common/PageHeader.vue';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';
    import dataExportService, { exportReportStatus } from '@/services/dataExportService';
    import exportService from '@/services/exportService';
    import Card from '@/components/common/Card.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import Tooltip from '@/components/common/Tooltip.vue';
    import ButtonIcon from '@/components/common/ButtonIcon.vue';
    import dateUtil from '@/utils/date';
    import ReportGroup from './parts/ReportGroup.vue';

    export default {
        name: 'DataExport',
        components: {
            PageHeader,
            ErrorMessage,
            ReportGroup,
            Card,
            DataTable,
            Tooltip,
            ButtonIcon,
        },
        data() {
            const now = new Date();
            return {
                selectedYear: getYear(now),
                selectedMonth: getMonth(now) + 1,
                availableYears: Array.from({ length: 5 }, (_, i) => now.getFullYear() - i),
                availableMonths: Array.from({ length: 12 }, (_, i) => ({
                    value: i + 1,
                    text: this.$t(`months.${i + 1}`),
                })),
                reports: [],
                priceReports: [],
                competitionReports: [],
                availabilityReports: [],
                error: null,
                loading: false,
                // to prevent loading state when polling interval
                disableLoadingState: false,
                generating: false,
                downloadingIds: [],
                pollingInterval: null,
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            computedLoading() {
                return this.loading && !this.disableLoadingState;
            },
            reportFields() {
                return [
                    { key: 'space.name', label: this.$t('name') },
                    { key: 'month', label: this.$t('month') },
                    { key: 'year', label: this.$t('year') },
                    { key: 'progress', label: '', sortable: false },
                    { key: 'actions', label: '', sortable: false },
                ];
            },
        },
        watch: {
            spaceId: {
                handler: 'fetchAllReports',
                immediate: true,
            },
        },
        methods: {
            async fetchAllReports() {
                this.loading = true;
                this.error = null;
                try {
                    const [main, price, comp, avail] = await Promise.all([
                        dataExportService.getExportSpacesReports(),
                        dataExportService.getExportProductsPriceReports({ urlParams: { spaceId: this.spaceId } }),
                        dataExportService.getExportCompetitionReports({ urlParams: { spaceId: this.spaceId } }),
                        dataExportService.getExportProductsAvailabilityReports({ urlParams: { spaceId: this.spaceId } }),
                    ]);

                    this.reports = (main.items || []).map(r => ({
                        ...r,
                        space: {
                            name: r.spaceId,
                        },
                        month: dateUtil.getMonthName(r.month - 1),
                    }));
                    this.priceReports = price.items || [];
                    this.competitionReports = comp.items || [];
                    this.availabilityReports = avail.items || [];
                } catch (err) {
                    this.error = err;
                } finally {
                    this.loading = false;
                }
            },
            async startGeneratingReport() {
                this.generating = true;
                this.error = null;
                try {
                    await dataExportService.postExportSpacesReports({
                        urlParams: { spaceId: this.spaceId },
                        requestParams: { year: this.selectedYear, month: this.selectedMonth },
                    });
                    this.fetchAllReports();
                } catch (err) {
                    this.error = err;
                } finally {
                    this.generating = false;
                }
            },
            async downloadReport(report, type) {
                this.downloadingIds.push(report.id);
                try {
                    const downloadsUrls = {
                        EXPORT_SPACES_REPORTS: '/api/v1/export-reports/{{id}}/download',
                        EXPORT_PRODUCTS_PRICE_REPORTS: '/api/v1/products-price-report-export/{{id}}/download',
                        EXPORT_COMPETITION_REPORTS: '/api/v1/competition-report-export/{{id}}/download',
                        EXPORT_PRODUCTS_AVAILABILITY_REPORTS: '/api/v1/products-availability-report-export/{{id}}/download',
                    };

                    await exportService.exportDataAuthorized(`${process.env.VUE_APP_API_URL}${downloadsUrls[type].replace('{{id}}', report.id)}`, `${report.space.name || 'report'} ${report.month || ''} ${report.year || ''}`, 'xlsx');
                } catch (e) {
                    this.$toastr.error(e.message, this.$t('errorOccurred'));
                } finally {
                    this.downloadingIds = this.downloadingIds.filter(id => id !== report.id);
                }
            },
            isReportPending(report) {
                return [exportReportStatus.PENDING, exportReportStatus.GENERATING].includes(report.status);
            },
            isReportDownloadable(report) {
                return [exportReportStatus.GENERATED, exportReportStatus.PARTIAL].includes(report.status);
            },
            isReportCorrupted(report) {
                return report.status === exportReportStatus.CORRUPTED;
            },
            async pollPendingReports() {
                this.disableLoadingState = true;
                const pending = this.reports.filter(r => this.isReportPending(r));
                if (pending.length > 0) {
                    await this.fetchAllReports();
                }
                this.disableLoadingState = false;
            },
        },
        created() {
            this.pollingInterval = setInterval(this.pollPendingReports, 10000);
        },
        beforeDestroy() {
            clearInterval(this.pollingInterval);
        },
    };
</script>
